<template>
  <a-list-item
    class="qa-issue"
    :class="{ 'qa-issue--navigable': navigationEnabled }"
    tabindex="0"
    @focusin="$emit('issue-hover', issue.id)"
    @focusout="$emit('issue-hover', null)"
    @mouseenter="$emit('issue-hover', issue.id)"
    @mouseleave="$emit('issue-hover', null)"
    @click="onNavigate">
    <a-popconfirm
      v-if="deletable"
      :title="deleteTitle"
      ok-text="Удалить"
      cancel-text="Отмена"
      ok-type="danger"
      @confirm="$emit('delete', issue.id)">
      <a-button
        class="qa-issue__delete"
        type="text"
        size="small"
        danger
        @click.stop>
        <close-outlined />
      </a-button>
    </a-popconfirm>

    <div class="qa-issue__body">
      <div class="qa-issue__top">
        <div class="qa-issue__title-row">
          <span class="qa-issue__title">{{ issue.title }}</span>
          <a-tag
            v-if="stageLabel"
            color="geekblue">
            {{ stageLabel }}
          </a-tag>
          <a-tag
            v-if="floorLabel"
            color="blue">
            {{ floorLabel }}
          </a-tag>
          <a-tag
            v-if="issue.kind === 'widget_bug'"
            color="magenta">
            Виджет клиента
          </a-tag>
          <a-tag
            v-if="statusLabel"
            :color="statusColor">
            {{ statusLabel }}
          </a-tag>
        </div>

        <rich-text-content
          v-if="issue.comment"
          class="qa-issue__comment"
          :value="issue.comment"
          @click="onCommentClick" />
        <div
          v-if="issue.author"
          class="qa-issue__meta">
          от: {{ issue.author.name }}
        </div>
        <div
          v-if="issue.resolution_comment"
          class="qa-issue__meta"
          @click="onCommentClick">
          <span class="qa-issue__meta-label">ответ:</span>
          <rich-text-content :value="issue.resolution_comment" />
        </div>
        <div
          v-if="issue.reopen_comment"
          class="qa-issue__meta qa-issue__meta--reopen"
          @click="onCommentClick">
          <span class="qa-issue__meta-label">не подтверждено:</span>
          <rich-text-content :value="issue.reopen_comment" />
        </div>
      </div>

      <image-viewer
        v-if="images.length"
        :images="images">
        <div
          class="qa-shots"
          :class="{ 'qa-shots--single': images.length === 1 }">
          <figure
            v-if="issue.before_screenshot"
            class="qa-shot">
            <img
              :src="issue.before_screenshot"
              alt="До">
            <figcaption>До</figcaption>
          </figure>
          <figure
            v-if="issue.after_screenshot"
            class="qa-shot">
            <img
              :src="issue.after_screenshot"
              alt="После">
            <figcaption>После</figcaption>
          </figure>
        </div>
      </image-viewer>

      <div
        v-if="showFixActions"
        class="qa-issue__actions"
        @click.stop>
        <a-button
          size="small"
          type="primary"
          @click="$emit('resolve', { issue, resolution: 'fixed' })">
          Исправил
        </a-button>
        <a-button
          size="small"
          @click="$emit('resolve', { issue, resolution: 'couldnt_fix' })">
          Не получилось
        </a-button>
      </div>

      <div
        v-if="showReviewActions"
        class="qa-issue__actions"
        @click.stop>
        <a-button
          size="small"
          type="primary"
          @click="$emit('confirm', issue.id)">
          Подтверждаю
        </a-button>
        <a-button
          size="small"
          danger
          @click="$emit('reopen', issue)">
          Не подтверждаю
        </a-button>
      </div>
    </div>
  </a-list-item>
</template>

<script setup>
import { computed } from 'vue';
import { CloseOutlined } from '@ant-design/icons-vue';
import ImageViewer from '@/Components/Common/ImageViewer.vue';
import RichTextContent from '@/Components/Common/RichTextContent.vue';

const props = defineProps({
  issue: { type: Object, required: true },
  mode: { type: String, required: true },
  navigationEnabled: { type: Boolean, default: true },
  deletable: { type: Boolean, default: false },
  // Режим наблюдателя: карточка показывает всё, но ничего не даёт изменить.
  readonly: { type: Boolean, default: false },
});

const emit = defineEmits(['issue-hover', 'goto', 'delete', 'resolve', 'confirm', 'reopen']);

const STAGE_LABELS = {
  jpg: 'JPG', svg: 'SVG', '2d': '2D', '3d': '3D', tour: '360',
};
const RESOLVED_STATUSES = ['fixed', 'couldnt_fix', 'confirmed', 'rejected'];

const isResolved = computed(() => RESOLVED_STATUSES.includes(props.issue.status));
const stageLabel = computed(() => STAGE_LABELS[props.issue.anchor?.stage] ?? null);

// Этаж показываем только когда он вообще может быть неоднозначным.
const floorLabel = computed(() => {
  const anchor = props.issue.anchor ?? {};
  if (anchor.floor_index === undefined || anchor.floor_index === null) return null;
  if (Number(anchor.floors_total ?? 0) < 2) return null;
  return `Этаж ${Number(anchor.floor_index) + 1}`;
});
const images = computed(
  () => [props.issue.before_screenshot, props.issue.after_screenshot].filter(Boolean),
);

const deleteTitle = computed(() => (
  props.issue.kind === 'recommendation' ? 'Удалить рекомендацию?' : 'Удалить замечание?'
));

const showFixActions = computed(
  () => !props.readonly && props.mode === 'fix' && props.issue.kind === 'error' && !isResolved.value,
);
// Подтверждать можно только то, что ниндзя отметил исправленным.
const showReviewActions = computed(
  () => !props.readonly && props.mode === 'review' && props.issue.status === 'fixed',
);

const statusLabel = computed(() => ({
  fixed: 'Исправлено',
  couldnt_fix: 'Не получилось',
  confirmed: 'Подтверждено',
  reopened: 'Возвращено',
  rejected: 'Снято',
}[props.issue.status] ?? null));

const statusColor = computed(() => ({
  fixed: 'green',
  couldnt_fix: 'volcano',
  confirmed: 'green',
  reopened: 'orange',
  rejected: 'default',
}[props.issue.status] ?? 'default'));

const onNavigate = () => {
  if (props.navigationEnabled) emit('goto', props.issue.id);
};

// Ссылка или картинка внутри комментария — самостоятельное действие,
// переход к замечанию по клику на карточку в этом случае только мешает.
const onCommentClick = (event) => {
  if (event.target.closest('a, img')) event.stopPropagation();
};
</script>

<style scoped>
.qa-issue {
  position: relative;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  padding: 12px !important;
  margin-bottom: 10px;
  background: #fff;
}
.qa-issue--navigable { cursor: pointer; }
.qa-issue__delete {
  position: absolute;
  top: 6px;
  right: 6px;
  z-index: 1;
}
.qa-issue__body { width: 100%; }
.qa-issue__top { margin-bottom: 10px; }
.qa-issue__title-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}
.qa-issue__title { font-weight: 500; }
.qa-issue__comment { color: rgba(0, 0, 0, 0.65); margin-top: 4px; }
.qa-issue__meta { color: rgba(0, 0, 0, 0.45); font-size: 12px; }
.qa-issue__meta-label { margin-right: 4px; }
.qa-issue__meta--reopen { color: #d4380d; }
.qa-issue__actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
}
.qa-shots {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}
.qa-shots--single {
  grid-template-columns: minmax(0, 220px);
  justify-content: center;
}
.qa-shot { margin: 0; text-align: center; }
.qa-shot img {
  display: block;
  width: 100%;
  aspect-ratio: 4 / 3;
  object-fit: contain;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  background: #fafafa;
  cursor: zoom-in;
}
.qa-shot figcaption { margin-top: 4px; color: rgba(0, 0, 0, 0.65); font-size: 12px; }
</style>
